import { type HeroTemplate, STONE_AGE_HEROES } from '@/data/heroes';
import { type Party } from '@/game/party';
import { canAdvanceToBronze, stoneAgeStatus } from '@/game/progression';

export class CodexPanel {
  private readonly el: HTMLElement;
  private readonly summary: HTMLElement;
  private readonly list: HTMLElement;
  private readonly heroes: HTMLElement;
  private readonly closeBtn: HTMLElement;
  private onClose: (() => void) | null = null;
  private visible = false;

  constructor(root: HTMLElement) {
    this.el = root;
    this.summary = mustGet(root, '.codex-points');
    this.list = mustGet(root, '.codex-list');
    this.heroes = mustGet(root, '.codex-heroes');
    this.closeBtn = mustGet(root, '.codex-close');

    this.closeBtn.addEventListener('click', () => this.onClose?.());
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.onClose?.();
    });
  }

  isVisible(): boolean {
    return this.visible;
  }

  show(party: Party, handlers: { onClose: () => void }): void {
    this.onClose = handlers.onClose;
    this.visible = true;
    this.el.classList.add('visible');
    this.render(party);
  }

  hide(): void {
    this.visible = false;
    this.el.classList.remove('visible');
    this.onClose = null;
  }

  refresh(party: Party): void {
    if (this.visible) this.render(party);
  }

  private render(party: Party): void {
    const statuses = stoneAgeStatus(party);
    const covered = statuses.filter((s) => s.covered).length;
    const total = statuses.length;
    const gateReady = canAdvanceToBronze(party);

    this.summary.textContent = gateReady
      ? `Stone Age complete — ${covered}/${total} milestones · Bronze Age awaits`
      : `Stone Age milestones: ${covered}/${total} covered`;

    this.list.replaceChildren();
    for (const s of statuses) {
      const li = document.createElement('li');
      li.className = s.covered ? 'codex-node unlocked' : 'codex-node locked';

      const head = document.createElement('div');
      head.className = 'codex-node-head';
      const name = document.createElement('span');
      name.className = 'codex-node-name';
      name.textContent = s.milestone.name;
      const tag = document.createElement('span');
      tag.className = 'codex-node-cost';
      tag.textContent = s.covered ? 'covered' : 'missing';
      head.appendChild(name);
      head.appendChild(tag);
      li.appendChild(head);

      const blurb = document.createElement('div');
      blurb.className = 'codex-node-blurb';
      blurb.textContent = s.milestone.blurb;
      li.appendChild(blurb);

      if (s.by.length > 0) {
        const by = document.createElement('div');
        by.className = 'codex-node-req';
        by.textContent = `by: ${s.by.map((h) => h.name).join(', ')}`;
        li.appendChild(by);
      }
      this.list.appendChild(li);
    }

    this.heroes.replaceChildren();
    for (const hero of STONE_AGE_HEROES) {
      const recruited = party.recruited.some((m) => m.templateId === hero.id);
      const li = document.createElement('li');
      li.className = recruited ? 'codex-hero recruited' : 'codex-hero unrecruited';
      li.appendChild(makeHeroCard(hero, recruited));
      this.heroes.appendChild(li);
    }
  }
}

const makeHeroCard = (hero: HeroTemplate, recruited: boolean): HTMLElement => {
  const card = document.createElement('div');
  const head = document.createElement('div');
  head.className = 'codex-hero-head';
  const name = document.createElement('span');
  name.className = 'codex-hero-name';
  name.textContent = hero.name;
  const status = document.createElement('span');
  status.className = 'codex-hero-status';
  status.textContent = recruited ? 'in party' : 'wild';
  head.appendChild(name);
  head.appendChild(status);
  card.appendChild(head);

  const era = document.createElement('div');
  era.className = 'codex-hero-era';
  era.textContent = hero.era;
  card.appendChild(era);

  const blurb = document.createElement('div');
  blurb.className = 'codex-hero-blurb';
  blurb.textContent = hero.blurb;
  card.appendChild(blurb);

  const move = document.createElement('div');
  move.className = 'codex-hero-move';
  move.textContent = `Moves: ${hero.moves.map((m) => m.name).join(', ')}`;
  card.appendChild(move);

  const contribs = document.createElement('div');
  contribs.className = 'codex-hero-contribs';
  contribs.textContent = `Contributions: ${hero.contributions.join(', ')}`;
  card.appendChild(contribs);

  return card;
};

const mustGet = (root: HTMLElement, selector: string): HTMLElement => {
  const el = root.querySelector<HTMLElement>(selector);
  if (el === null) throw new Error(`codex-panel: missing element ${selector}`);
  return el;
};
